import { useEffect } from "react";

/** schema.org AggregateRating from the admin's Google rating — only once both rating and review count are set. */
export const aggregateRating = (google) =>
  google?.rating && google?.count
    ? { "@type": "AggregateRating", ratingValue: Number(google.rating), reviewCount: Number(google.count), bestRating: 5, worstRating: 1 }
    : null;

/** Put the admin Google rating on every HomeAndConstructionBusiness JSON-LD block in <head>
 *  (the static homepage one and the town page's own), or take it off if the rating was cleared. */
export function useBusinessRating(google, page) {
  useEffect(() => {
    if (google === undefined) return;
    const rating = aggregateRating(google);
    document.head.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
      try {
        const obj = JSON.parse(el.textContent);
        if (obj["@type"] !== "HomeAndConstructionBusiness") return;
        if (rating) obj.aggregateRating = rating;
        else delete obj.aggregateRating;
        el.textContent = JSON.stringify(obj);
      } catch {
        /* not a JSON-LD block we manage */
      }
    });
  }, [google, page]); // page: re-apply after a town-to-town move swaps the page's own block (call after useSeo)
}

// Upsert a <meta> tag; returns a restore fn that puts the DOM back exactly as it was.
const upsertMeta = (key, keyType, content) => {
  let el = document.head.querySelector(`meta[${keyType}="${key}"]`);
  const created = !el;
  const prev = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyType, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) el.remove();
    else if (prev != null) el.setAttribute("content", prev);
  };
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  const prev = el ? el.getAttribute("href") : null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (created) el.remove();
    else if (prev != null) el.setAttribute("href", prev);
  };
};

// Client-side <head> manager for SPA pages. Sets title/description/canonical/OG and injects
// a page-specific JSON-LD block, then fully restores the previous head on unmount so the
// landing page and other routes keep their original metadata untouched.
export function useSeo({ title, description, canonical, ogTitle, ogDescription, ogUrl, image, jsonLd }) {
  useEffect(() => {
    const restores = [];
    const prevTitle = document.title;
    if (title) document.title = title;
    if (description) restores.push(upsertMeta("description", "name", description));
    if (ogTitle) restores.push(upsertMeta("og:title", "property", ogTitle));
    if (ogDescription) restores.push(upsertMeta("og:description", "property", ogDescription));
    if (ogUrl) restores.push(upsertMeta("og:url", "property", ogUrl));
    if (canonical) restores.push(upsertLink("canonical", canonical));
    if (image) {
      restores.push(upsertMeta("og:image", "property", image));
      restores.push(upsertMeta("twitter:image", "name", image));
    }
    if (ogTitle) restores.push(upsertMeta("twitter:title", "name", ogTitle));
    if (ogDescription) restores.push(upsertMeta("twitter:description", "name", ogDescription));

    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.setAttribute("data-page-jsonld", "true");
      ldEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(ldEl);
    }

    return () => {
      document.title = prevTitle;
      restores.forEach((fn) => fn());
      if (ldEl && ldEl.parentNode) ldEl.remove();
    };
  }, [title, description, canonical, ogTitle, ogDescription, ogUrl, image, jsonLd]);
}
